import React, { useEffect, useContext } from "react";
import { usePlaidLink } from "react-plaid-link";
import { auth, db } from "../../firebase/config";
import { doc, setDoc } from "firebase/firestore";
import { apiFetch } from "../../utils/api";

import Context from "../../Context";

const Link = () => {
  const { linkToken, isPaymentInitiation, isCraProductsExclusively, dispatch, onLinkExit } =
    useContext(Context);

  const onSuccess = React.useCallback(
    (public_token: string) => {
      // If the access_token is needed, send public_token to server
      const exchangePublicTokenForAccessToken = async () => {
        const response = await apiFetch("/api/set_access_token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          },
          body: `public_token=${public_token}`,
        });
        if (!response.ok) {
          dispatch({
            type: "SET_STATE",
            state: {
              itemId: `no item_id retrieved`,
              accessToken: `no access_token retrieved`,
              isItemAccess: false,
            },
          });
          return;
        }
        const data = await response.json();
        
        // Store access token in Firestore for the current user
        if (auth.currentUser) {
          try {
            const userDocRef = doc(db, "users", auth.currentUser.uid);
            
            // Use setDoc with merge to create document if it doesn't exist
            await setDoc(userDocRef, {
              plaidAccessToken: data.access_token,
              plaidItemId: data.item_id,
              plaidConnectedAt: new Date().toISOString(),
              email: auth.currentUser.email,
            }, { merge: true });
            
            console.log("✅ Access token saved to Firestore");
          } catch (error) {
            console.error("Error saving access token to Firestore:", error);
          }
        }

        dispatch({
          type: "SET_STATE",
          state: {
            itemId: data.item_id,
            accessToken: data.access_token,
            isItemAccess: true,
          },
        });
      };

      // 'payment_initiation' products do not require the public_token to be exchanged for an access_token.
      if (isPaymentInitiation) {
        dispatch({ type: "SET_STATE", state: { isItemAccess: false } });
      } else if (isCraProductsExclusively) {
        // When only CRA products are enabled, only user_token is needed. access_token/public_token exchange is not needed.
        dispatch({ type: "SET_STATE", state: { isItemAccess: false } });
      } else {
        exchangePublicTokenForAccessToken();
      }

      dispatch({ type: "SET_STATE", state: { linkSuccess: true } });
      window.history.pushState("", "", "/");
    },
    [dispatch, isPaymentInitiation, isCraProductsExclusively]
  );

  const onExit = React.useCallback(() => {
    // Call the exit callback if provided (to hide overlay)
    if (onLinkExit) {
      onLinkExit();
    }
  }, [onLinkExit]);

  let isOauth = false;
  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken!,
    onSuccess,
    onExit,
  };

  if (window.location.href.includes("?oauth_state_id=")) {
    // TODO: figure out how to delete this ts-ignore
    // @ts-ignore
    config.receivedRedirectUri = window.location.href;
    isOauth = true;
  }

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (isOauth && ready) {
      open();
    }
  }, [ready, open, isOauth]);

  // Auto-open when ready (for direct launch from Profile)
  useEffect(() => {
    if (ready && !isOauth) {
      open();
    }
  }, [ready, open, isOauth]);

  // Don't render the button - Plaid opens automatically
  return null;
};

Link.displayName = "Link";

export default Link;
